import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, KeyRound, Loader2, MonitorSmartphone, ShieldOff } from 'lucide-react';
import ConfirmModal from '../shared/ConfirmModal';
import { useToast } from '../shared/Toast';
import { SheetSection } from '../settings/sheet';
import { NewApiKey, PrintingAgent, printingApi } from './printingApi';
import { DANGER_GHOST_BTN, GHOST_BTN, Notice, StatusDot } from './printingUi';
import { fmtClock, fmtDateClock } from '../../utils/athensDate';

/**
 * «Υπολογιστής ταμείου»: αν ο print agent είναι συνδεδεμένος, το .env που χρειάζεται (με αντιγραφή),
 * «Νέο κλειδί» (το πλήρες κλειδί φαίνεται ΜΙΑ φορά, εδώ, ως την έξοδο από τη σελίδα) και «Ανάκληση».
 */

interface Props {
  storeId: number;
  agent: PrintingAgent;
  /** Μετά από νέο κλειδί/ανάκληση: η σελίδα ξαναδιαβάζει την κατάσταση. */
  onChanged: () => void;
}

/** Ώρα αν είναι σήμερα, αλλιώς ημερομηνία και ώρα — στη ζώνη του browser (ταμείο = Ελλάδα). */
const sinceText = (iso: string | null, lang: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const locale = lang.startsWith('en') ? 'en-GB' : 'el-GR';
  const sameDay = d.toDateString() === new Date().toDateString();
  // 24ωρη χωρίς «24:15» (το hour12:false με el-GR το έδινε μετά τα μεσάνυχτα).
  return sameDay
    ? fmtClock(d, { zone: 'local', locale })
    : fmtDateClock(d, { zone: 'local', locale });
};

const AgentSection: React.FC<Props> = ({ storeId, agent, onChanged }) => {
  const { t, i18n } = useTranslation(['printing']);
  const { showToast } = useToast();
  const [fresh, setFresh] = useState<NewApiKey | null>(null);
  const [busy, setBusy] = useState<'key' | 'revoke' | null>(null);
  const [confirm, setConfirm] = useState<'key' | 'revoke' | null>(null);
  const { env } = agent;
  const keySet = env.api_key_set || fresh !== null;

  const baseLines = [`SERVER_URL=${env.server_url}`, `STORE_ID=${env.store_id}`];
  const envText = fresh
    ? (fresh.env_block || [...baseLines, `API_KEY=${fresh.api_key}`].join('\n'))
    : [...baseLines, `API_KEY=${env.api_key_set ? (env.api_key_masked || '••••••••') : ''}`].join('\n');

  const copy = async () => {
    // Χωρίς το πλήρες κλειδί δεν αντιγράφεται η μάσκα (θα χαλούσε το .env): μόνο οι δύο πρώτες γραμμές.
    const value = fresh ? envText : baseLines.join('\n');
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(value);
      showToast('success', fresh ? t('printing:agent.copied') : t('printing:agent.copiedNoKey'));
    } catch {
      showToast('error', t('printing:agent.copyFailed'));
    }
  };

  const createKey = async () => {
    setConfirm(null);
    setBusy('key');
    const r = await printingApi.createApiKey(storeId);
    setBusy(null);
    if (!r.ok || !r.data?.api_key) {
      showToast('error', r.error || t('printing:agent.newKeyFailed'));
      return;
    }
    setFresh({ api_key: r.data.api_key, env_block: r.data.env_block || '' });
    onChanged();
  };

  const revoke = async () => {
    setConfirm(null);
    setBusy('revoke');
    const r = await printingApi.revokeApiKey(storeId);
    setBusy(null);
    if (!r.ok) {
      showToast('error', r.error || t('printing:agent.revokeFailed'));
      return;
    }
    setFresh(null);
    showToast('success', t('printing:agent.revoked'));
    onChanged();
  };

  // Χωρίς κλειδί δεν υπάρχει τίποτα να χαλάσει: δημιουργία χωρίς ερώτηση.
  const askNewKey = () => (keySet ? setConfirm('key') : createKey());

  const since = sinceText(agent.connected_since, i18n.language || 'el');
  const details = [
    agent.pc_name ? t('printing:agent.pc', { name: agent.pc_name }) : null,
    agent.version ? t('printing:agent.version', { version: agent.version }) : t('printing:agent.versionUnknown'),
    since ? t('printing:agent.since', { time: since }) : null,
  ].filter(Boolean).join(' · ');

  return (
    <SheetSection id="printing-agent" title={t('printing:agent.title')} hint={t('printing:agent.hint')} testId="agent-section">
      <div className="flex items-start gap-3" data-testid="agent-status" data-connected={agent.connected ? 'true' : 'false'}>
        <span className="mt-[7px]"><StatusDot on={agent.connected} /></span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-t1">
            {agent.connected ? t('printing:agent.connected') : t('printing:agent.offline')}
          </p>
          {agent.connected
            ? <p className="mt-0.5 text-[13px] text-t3 break-words">{details}</p>
            : <p className="mt-0.5 text-[13px] text-t3">{t('printing:agent.offlineHint')}</p>}
        </div>
      </div>

      {!keySet && (
        <div className="mt-4"><Notice testId="agent-no-key">{t('printing:agent.noKeyHint')}</Notice></div>
      )}

      {fresh && (
        <div className="mt-4" role="alert">
          <Notice testId="agent-new-key">{t('printing:agent.newKeyDone')}</Notice>
        </div>
      )}

      <div className="mt-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <span id="printing-env-label" className="text-sm font-medium text-t2">{t('printing:agent.envLabel')}</span>
          <button type="button" onClick={copy} className={GHOST_BTN} aria-describedby="printing-env-label">
            <Copy className="w-4 h-4" aria-hidden="true" />{t('printing:agent.copy')}
          </button>
        </div>
        <pre
          data-testid="agent-env"
          className="m-0 px-3.5 py-3 rounded-xl border border-line bg-surface-2 text-[13px] leading-[20px] font-mono text-t1 whitespace-pre-wrap break-all"
        >
          {envText}
        </pre>
        {!fresh && env.api_key_set && <p className="xs-note">{t('printing:agent.keyMaskedNote')}</p>}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={askNewKey} disabled={busy !== null} className={GHOST_BTN}>
          {busy === 'key' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <KeyRound className="w-4 h-4" aria-hidden="true" />}
          {t('printing:agent.newKey')}
        </button>
        {keySet && (
          <button type="button" onClick={() => setConfirm('revoke')} disabled={busy !== null} className={DANGER_GHOST_BTN}>
            {busy === 'revoke' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <ShieldOff className="w-4 h-4" aria-hidden="true" />}
            {t('printing:agent.revoke')}
          </button>
        )}
      </div>

      <p className="mt-5 pt-4 border-t border-line flex items-start gap-2 text-[13px] leading-[19px] text-t3">
        <MonitorSmartphone className="w-4 h-4 mt-0.5 shrink-0 text-t4" aria-hidden="true" />
        <span>{t('printing:agent.vfd')}</span>
      </p>

      <ConfirmModal
        isOpen={confirm === 'key'}
        title={t('printing:agent.newKeyConfirmTitle')}
        message={t('printing:agent.newKeyConfirmMessage')}
        confirmLabel={t('printing:agent.newKey')}
        cancelLabel={t('printing:cancel')}
        variant="warning"
        onConfirm={createKey}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmModal
        isOpen={confirm === 'revoke'}
        title={t('printing:agent.revokeConfirmTitle')}
        message={t('printing:agent.revokeConfirmMessage')}
        confirmLabel={t('printing:agent.revoke')}
        cancelLabel={t('printing:cancel')}
        variant="danger"
        onConfirm={revoke}
        onCancel={() => setConfirm(null)}
      />
    </SheetSection>
  );
};

export default AgentSection;
